import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { BotRun } from "../../../../api/graphql";
import { BotRunListStore } from "../../store";
import { workerEventDisplayId } from "../../../workerEvent/store/WorkerEventCommonStore";
import { userDisplayId } from "../../../user/store/UserCommonStore";
import { botPhaseDisplayId } from "../../../botPhase/store/BotPhaseCommonStore";
import { BotPhasePanel } from "./BotPhasePanel";
import { BotErrorPanel } from "./BotErrorPanel";
import {
  VisibilityIcon,
  DeleteForeverIcon,
  StopCircleIcon,
  DangerousIcon,
} from "../../../../icons";
import { getApiAsync } from "../../../../api";

export type GridExpressProps = { listStore?: BotRunListStore };

export const BotRunListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Detención de bots en ejecución (SysAdmin): cooperativa primero; forzada
    // para colgados — marca el run failed y el trámite cae a su failure-state,
    // donde los botones de recovery de siempre permiten relanzar.
    const cancelBotRun = async (row: BotRun, force: boolean) => {
      const label = force
        ? `¿Forzar la detención del bot "${row.botType}"? El run se marca como fallido y el trámite pasa a su estado de falla para poder relanzarlo.`
        : `¿Solicitar la detención del bot "${row.botType}"? Se detiene de forma segura al finalizar la fase en curso.`;
      if (!window.confirm(label)) return;
      try {
        const api = await getApiAsync(rootStore.authStore as any, (rootStore as any).activityStore);
        await (api as any).botRunCancel({ botRunId: row.id, force });
        store.loadPage(true);
      } catch (e: any) {
        console.error("botRunCancel failed", e);
      }
    };

    // Row actions
    const rowActions: DataGridRowAction<BotRun>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: BotRun, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: "Detener",
          onClick: (row: BotRun) => void cancelBotRun(row, false),
          icon: <StopCircleIcon fontSize="small" />,
          isVisible: (row: BotRun) => row.status === "running",
          isEnabled: (row: BotRun) => !row.cancellationRequestedAt,
        },
        {
          label: "Forzar detención",
          onClick: (row: BotRun) => void cancelBotRun(row, true),
          icon: <DangerousIcon fontSize="small" />,
          isVisible: (row: BotRun) => row.status === "running",
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: BotRun) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<BotRun>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "botType") {
          return {
            ...col,
            id: "botType",
            accessorFn: (row: BotRun) => {
              const v = (row as any).botType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.BotTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "startedAt") {
          return {
            ...col,
            id: "startedAt",
            accessorFn: (row: BotRun) =>
              row.startedAt
                ? new Date(row.startedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "completedAt") {
          return {
            ...col,
            id: "completedAt",
            accessorFn: (row: BotRun) =>
              row.completedAt
                ? new Date(row.completedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "workerEvent") {
          return {
            ...col,
            id: "workerEvent",
            accessorFn: (row: BotRun) =>
              row.workerEvent
                ? workerEventDisplayId(row.workerEvent, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "triggeredBy") {
          return {
            ...col,
            id: "triggeredBy",
            accessorFn: (row: BotRun) =>
              row.triggeredBy ? userDisplayId(row.triggeredBy, langStore) : "-",
          };
        }
        if (col.accessorKey === "cancellationRequestedAt") {
          return {
            ...col,
            id: "cancellationRequestedAt",
            accessorFn: (row: BotRun) =>
              row.cancellationRequestedAt
                ? new Date(row.cancellationRequestedAt).toLocaleString(
                    "es-ES",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )
                : "-",
          };
        }
        if (col.accessorKey === "cancellationRequestedBy") {
          return {
            ...col,
            id: "cancellationRequestedBy",
            accessorFn: (row: BotRun) =>
              row.cancellationRequestedBy
                ? userDisplayId(row.cancellationRequestedBy, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "phases") {
          return {
            ...col,
            id: "phases",
            accessorFn: (row: BotRun) =>
              row.phases?.length
                ? row.phases
                    .map((x: any) => botPhaseDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "errors") {
          return {
            ...col,
            id: "errors",
            accessorFn: (row: BotRun) =>
              row.errors?.length ? `${row.errors.length} item(s)` : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.botRunCurrentPage || [];

    return (
      <Box id={"ListOfBotRun"}>
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          multiSelectMode={store.multiSelectMode}
          selectedRowIds={store.selectedRowIds}
          onSelectedRowIdsChange={(ids) => store.setSelectedRowIds(ids)}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ pl: 4, pr: 2, pb: 2, backgroundColor: "action.hover" }}>
              <BotPhasePanel parentId={row.original.id} />
              <BotErrorPanel parentId={row.original.id} />
            </Box>
          )}
        />
      </Box>
    );
  }
);
